import { throwError,  Subject, Observable, of } from 'rxjs';
import { map, catchError, concatMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoginService } from './login.service';
import { AngularFireAuth } from '@angular/fire/auth';

@Injectable()
export class WebServicesService {
  private postPutDeleteCallback = () => { };

  constructor(
    private _http: HttpClient,
    private _login: LoginService,
    public _afAuth: AngularFireAuth,
  ) { }

  private getFbToken(): Observable<string> {
    const o = this._afAuth.idToken;

    if (!o) {
      return of(null);
    }

    return o;
  }


  private getDefaultHeaders(): Observable<any> {
    const s = new Subject<any>();
      this.getFbToken().subscribe(idToken => {

      s.next({
        'X-Firebase-IdToken' : idToken
      });

      s.complete();
    });

    return s.asObservable();
  }

  // action is a function which takes 2 functions as arguments: cb401, cbError
  // action returns an observable (NOT SUBSCRIBED)
  //      cb401: function called when 401 occurs: returns an Observable (NOT SUBSCRIBED)
  //             which is in fact used as the result of the action function
  //      cbError/errorFn: called when another error occurs. DOES NOT RETURN ANYTHING
  private httpCallMethod(action, errorFn: (error) => void) {

    // cb401 for the 2nd round (when already refreshed the token)
    // if you successfully refresh the token and immediatelly after that you get a 401 with the new token
    // then you'd better be logged out because something REALLY strange happened.
    // as 401 is catched by errorFn and logs out user, we pass a 401
    const logoutCb = () => {
      errorFn({ status: 401 });
      return throwError(401);
    };

    // This is cb401 of action
    // gets executed when 401 occurs:
    // - refresh the token and, when refreshed calls action again, but with a 'synk' cb401 (i.e. logoutCb)
    //   so that it will not get in endless loop
    const retryCb = () => {

      // we create a new Subject(i.e. a willbe Observable)
      // when we get results from the action, we add them to this observable, which gets subscribed by the caller
      const csbj = new Subject<any>();

      // perform token refresh
      this._login.doGetNewTokenPair().subscribe(sc => {
        if (sc === 200) {
          action(logoutCb, errorFn).subscribe(res => {
            csbj.next(res);
            csbj.complete();
          });
        } else if (sc === 401) { // Dovrebbe essere 401, ma in GYb restituisce 400 e non c'è modo di capire perchè
          logoutCb();
        } else {
          errorFn({ status: sc });
        }
      });

      return csbj.asObservable();

    };

    // actually call the method with the callbacks
    return action(retryCb, errorFn);
  }

  public getWithoutCache(errorFn: (error) => void, url: string) {
    return this.httpCallMethod((cb401, cbError) => {

      return this.getDefaultHeaders()
      .pipe(concatMap(headers => this._http.get(url, { headers: headers })
      .pipe(catchError(error => {

        if (error.status === 401) {
          return cb401();
        } else {
          cbError(error);
          return throwError(error.status);
        }
      }))));

    }, errorFn);
  }

  public putForm(errorFn: (error) => void, url: string, data) {
    return this.httpCallMethod((cb401, cbError) => {
      return this.submitFormHttp(errorFn, cb401, url, data, 'PUT');
    }, errorFn);
  }

  public postForm(errorFn: (error) => void, url: string, data) {
    return this.httpCallMethod((cb401, cbError) => {
      return this.submitFormHttp(errorFn, cb401, url, data, 'POST');
    }, errorFn);
  }

  public putJson(errorFn: (error) => void, url: string, data) {
    return this.httpCallMethod((cb401, cbError) => {
      return this.jsonFormHttp(errorFn, cb401, url, data, 'PUT');
    }, errorFn);
  }

  public postJson(errorFn: (error) => void, url: string, data) {
    return this.httpCallMethod((cb401, cbError) => {
      return this.jsonFormHttp(errorFn, cb401, url, data, 'POST');
    }, errorFn);
  }


  public deleteHttp(errorFn: (error) => void, url: string) {
    return this.httpCallMethod((cb401, cbError) => {
      return this.getDefaultHeaders()
      .pipe(concatMap(headers => this._http.delete(url, { headers: headers }).pipe(catchError(error => {
        if (error.status === 401) {
          return cb401();
        } else {
          errorFn(error);
          return throwError(error.status);
        }
      }),
      map(res => {
        this.postPutDeleteCallback();
        return res;
      }))));

    }, errorFn);
  }

  private jsonFormHttp(errorFn: (error) => void, cb401, url: string, payload, method: string, contentType?: string) {
    return this.getDefaultHeaders() .pipe(concatMap(headers => {
      if (contentType) {
        headers['Content-Type'] = contentType;
      } else {
        headers['Content-Type'] = 'application/json';
      }

      if (method === 'POST') {
        return this._http.post(url, payload, { headers: headers }).pipe(catchError(error => {
          if (error.status === 401) {
            return cb401();
          } else {
            errorFn(error);
            return throwError(error.status);
          }
        }),
        map(res => {
          this.postPutDeleteCallback();
          return res;
        }));
      } else if (method === 'PUT') {
        return this._http.put(url, payload, { headers: headers }).pipe(catchError(error => {
          if (error.status === 401) {
            return cb401();
          } else {
            errorFn(error);
            return throwError(error.status);
          }
        }),
        map(res => {
          this.postPutDeleteCallback();
          return res;
        }));
      }
    }));
  }

  private submitFormHttp(errorFn: (error) => void, cb401, url: string, data, method: string) {

    return this.getDefaultHeaders()
    .pipe(concatMap(headers => {


    headers['Content-Type'] = 'application/x-www-form-urlencoded';

    let payload = '';

    for (const k in data) {
      if (data.hasOwnProperty(k)) {
        if ('' === payload) {
          payload = k + '=' + data[k];
        } else {
          payload = payload + '&' + k + '=' + data[k];
        }
      }
    }

    payload = encodeURI(payload);

    if (method === 'POST') {
      return this._http.post(url, payload, { headers: headers }).pipe(catchError(error => {
        if (error.status === 401) {
          return cb401();
        } else {
          errorFn(error);
          return throwError(error.status);
        }
      }),
      map(res => {
        this.postPutDeleteCallback();
        return res;
      }));
    } else if (method === 'PUT') {
      return this._http.put(url, payload, { headers: headers }).pipe(catchError(error => {
        if (error.status === 401) {
          return cb401();
        } else {
          errorFn(error);
          return throwError(error.status);
        }
      }),
      map(res => {
        this.postPutDeleteCallback();
        return res;
      }));
    }
    }));
  }
}
